import { type ComponentProps, useEffect, useRef } from "react";
import { cn } from "../cn";

interface CheckboxProps extends Omit<ComponentProps<"input">, "type"> {
  /** Some of the items it stands for are checked ("select all" with a partial selection). */
  indeterminate?: boolean;
}

/**
 * Native checkbox: keyboard and screen reader behaviour as is. The label around it is the pointer target, as tall as
 * the other controls on touch screens (ADR-050, point 4); the text comes from `children`, or from `aria-label`.
 */
export function Checkbox({ className, indeterminate = false, children, ...props }: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  // `indeterminate` is a property of the element, not an attribute
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label
      className={cn(
        "inline-flex shrink-0 items-center gap-2 text-sm pointer-coarse:min-h-(--control-h) pointer-coarse:min-w-(--control-h)",
        !children && "justify-center",
        className,
      )}
    >
      <input ref={ref} type="checkbox" className="size-4 accent-(--brand) disabled:opacity-50" {...props} />
      {children}
    </label>
  );
}
